import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ChartEnums } from "models/enums";
import { ChartDataSet, ChartViewProps } from "models/propTypes/chartProps";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import useTreeDataHandler from "./useTreeDataHandler";
import TreeChart from "./tree/treeChart";
import useMountedState from "../useMountedState";
import DoughnutChart from "./doughnutChart";
import BarChart from "./barChart";
import NumberChart from "./numberChart";
import LineChart from "./lineChart";
import useChartsWithCache from "./hooks/useChartsWithCache";
import { getBarPointDateRange } from "./utils/getBarPointDateRange";
import { ChartSkeletonSelector } from "components/common/skeletons";
//#endregion

const Chart: React.FC<ChartViewProps> = (props) => {
  const { getChartByFilter } = useChartsWithCache();
  const {
    id,
    chartAPIKey,
    apiToChartHandler,
    iconHandler,
    legendFormatter,
    labelFormatter,
    toolTipFormatter,
    customLabelsHandler,
    onChartSuccessHandler,
    hideLegend,
    filter: staticFilter = {},
    showTotal,
    doughnutChartLegendType,
    doughnutChartColorPalette,
    showDoughnutChartMiddleText,
    numberChartIcon,
    dataUnit,
    yAxisTickUnit,
    overrideLabelFontSize,
    chartCacheKey,
    onPeriodClick,
  } = props;

  const { getMounted } = useMountedState();
  const [result, setResult] = React.useState<{ data: WintSuperkollAPIModel.ReportWidget; key: number }>();
  const chartId = `${chartAPIKey}${`_${id ?? ""}`}`;
  const frozenKeyRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    getChartByFilter(chartAPIKey, staticFilter, chartCacheKey).then((res) => {
      if (getMounted()) {
        setResult({ data: res!, key: (result?.key ?? 0) + 1 });
        onChartSuccessHandler && onChartSuccessHandler(res, staticFilter);
      }
    });
  }, [staticFilter, chartAPIKey]);

  const chartData = result?.data;

  const chartTitle = chartData
    ? typeof props.chartTitle === "function"
      ? props.chartTitle(chartData)
      : props.chartTitle
    : "";

  const appendIndex = (index: number, label?: string) => (label !== undefined ? `${label};${index}` : undefined);

  const { apiToTreeDataHandler } = useTreeDataHandler(iconHandler);

  const getLabels = (dataLabels?: string[], labelFormatter?: (label?: string, totalLabelCount?: number) => string) =>
    (labelFormatter ? dataLabels?.map((l) => labelFormatter(l, dataLabels?.length)) : dataLabels) ?? [];

  const onBarOrLineClick = React.useCallback(
    (index: number, datasetIndex: number, xValue?: any, label?: any) => {
      if (!onPeriodClick) return;
      const ds = chartData?.data?.[datasetIndex];
      if (ds && ds.startDate) {
        const range = getBarPointDateRange(ds.startDate, index, ds.dataFrequency);
        if (range) {
          onPeriodClick({ start: range.start, end: range.end, index, datasetIndex, label });
        }
      }
    },
    [onPeriodClick, chartData]
  );

  const component = React.useMemo(
    () =>
      chartData ? (
        chartData.type === WintSuperkollAPIModel.ReportWidgetType.Pie ? (
          <DoughnutChart
            key={frozenKeyRef.current ?? result?.key}
            id={chartId}
            title={chartTitle}
            useClickableChart={props.useClickableChart}
            legendType={doughnutChartLegendType ?? ChartEnums.LegendType.SMALL}
            colorPaletteType={doughnutChartColorPalette ?? ChartEnums.DoughnutChartColorPalette.DEFAULT}
            datasets={
              apiToChartHandler
                ? (apiToChartHandler(chartData) as ChartDataSet[] | undefined)
                : chartData.data?.map((d, index) => ({
                    label: labelFormatter
                      ? labelFormatter(chartData?.dataLabels ? chartData.dataLabels[index] : d.name)
                      : chartData?.dataLabels
                        ? chartData.dataLabels[index]
                        : d.name,
                    data: d.dataPoints ? d.dataPoints[0] : 0,
                  }))
            }
            middleText={showDoughnutChartMiddleText ? `${chartData.numOfRecords ?? 0} st` : undefined}
            hideLegend={hideLegend}
            showTotal={showTotal}
            dataUnit={dataUnit}
            onClick={(index, label) => {
              if (frozenKeyRef.current == null && result?.key != null) {
                frozenKeyRef.current = result.key;
              }
              props.onDataPointClick && props.onDataPointClick(index, label);
            }}
            onBackgroundClick={() => {
              if (frozenKeyRef.current != null) {
                frozenKeyRef.current = null;
              }
              props.onBackgroundClick && props.onBackgroundClick();
            }}
          />
        ) : chartData.type === WintSuperkollAPIModel.ReportWidgetType.Bar ? (
          <BarChart
            key={result?.key}
            id={chartId}
            useClickableChart={props.useClickableChart}
            labels={
              customLabelsHandler
                ? customLabelsHandler(chartData)
                : chartData.comparableDataLabels && chartData.dataLabels
                  ? [
                      getLabels(chartData.dataLabels, labelFormatter),
                      getLabels(chartData.comparableDataLabels, labelFormatter),
                    ]
                  : getLabels(chartData.dataLabels, labelFormatter)
            }
            tooltips={
              toolTipFormatter
                ? chartData.comparableDataLabels && chartData.dataLabels
                  ? [
                      getLabels(chartData.dataLabels, toolTipFormatter),
                      getLabels(chartData.comparableDataLabels, toolTipFormatter),
                    ]
                  : getLabels(chartData.dataLabels, toolTipFormatter)
                : undefined
            }
            title={chartTitle}
            datasets={
              apiToChartHandler
                ? (apiToChartHandler(chartData) as ChartDataSet[] | undefined)
                : chartData.data?.map((chart, index) => ({
                    label: legendFormatter
                      ? legendFormatter(index, chart.name, chart.startDate, chart.endDate)
                      : chart.name,
                    data: chart.dataPoints?.map((d, index) => ({
                      x: appendIndex(
                        index,
                        labelFormatter
                          ? labelFormatter(chartData?.dataLabels && chartData.dataLabels[index])
                          : chartData?.dataLabels && chartData.dataLabels[index]
                      ),
                      y: d,
                    })),
                  }))
            }
            maintainAspectRatio={false}
            yAxisTickUnit={yAxisTickUnit}
            hideLegend={hideLegend}
            dataUnit={dataUnit}
            legendPortalId={props.legendPortalId}
            overrideLabelFontSize={overrideLabelFontSize}
            onClick={onBarOrLineClick}
            onBackgroundClick={() => {
              props.onBackgroundClick && props.onBackgroundClick();
            }}
          />
        ) : chartData.type === WintSuperkollAPIModel.ReportWidgetType.Line ? (
          <LineChart
            key={result?.key}
            id={chartId}
            useClickableChart={props.useClickableChart}
            labels={
              (customLabelsHandler
                ? customLabelsHandler(chartData)
                : labelFormatter
                  ? chartData.dataLabels?.map((l) => labelFormatter(l))
                  : chartData.dataLabels) ?? []
            }
            title={chartTitle}
            datasets={
              apiToChartHandler
                ? (apiToChartHandler(chartData) as ChartDataSet[] | undefined)
                : chartData.data?.map((chart, index) => ({
                    label: legendFormatter
                      ? legendFormatter(index, chart.name, chart.startDate, chart.endDate)
                      : chart.name,
                    data: chart.dataPoints?.map((d, index) => ({
                      x: appendIndex(
                        index,
                        labelFormatter
                          ? labelFormatter(chartData?.dataLabels && chartData.dataLabels[index])
                          : chartData?.dataLabels && chartData.dataLabels[index]
                      ),
                      y: d,
                    })),
                  }))
            }
            maintainAspectRatio={false}
            yAxisTickUnit={yAxisTickUnit}
            hideLegend={hideLegend}
            dataUnit={dataUnit}
            legendPortalId={props.legendPortalId}
            overrideLabelFontSize={overrideLabelFontSize}
            onClick={onBarOrLineClick}
            onBackgroundClick={() => {
              props.onBackgroundClick && props.onBackgroundClick();
            }}
          />
        ) : chartData.type === WintSuperkollAPIModel.ReportWidgetType.Number ? (
          <NumberChart
            key={result?.key}
            id={chartId}
            icon={numberChartIcon ?? IconDefinitionConstants.WEmpty}
            data={chartData.data ? (chartData.data[0].dataPoints ? chartData.data[0].dataPoints[0] : 0) : 0}
            dataUnit={dataUnit}
            title={chartTitle}
          />
        ) : chartData.type === WintSuperkollAPIModel.ReportWidgetType.Tree ? (
          <TreeChart key={result?.key} id={chartId} data={apiToTreeDataHandler(chartData)}></TreeChart>
        ) : (
          <></>
        )
      ) : (
        <ChartSkeletonSelector chartType={props.useSkeleton} />
      ),
    [result]
  );

  return component;
};

export default Chart;
